import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useChatSubmitNavigate } from '~/hooks/use-chat-submit-navigate/useChatSubmitNavigate';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { Source } from '~/stores/use-chat/use-chat.types';
import { useChat } from '~/stores/use-chat/useChat';

import { ChatDropdown } from '../chat-dropdown-item/ChatDropdownItem';
import { TagSubmit } from '../tag-submit/TagSubmit';

export interface ChatSubmitProps {
  placeholder?: string;
  submitCallback?: (message: string) => void;
  stopResponseCallback?: () => void;
  isSimpleVersion?: boolean;
  chatContext?: {
    target: Source;
    documents?: string[];
  };
  disableEditingOnMessagePending?: boolean;
  persistInputTextOnSubmit?: boolean;
  clearDocumentContextOnSubmit?: boolean;
}

export const ChatSubmit = ({
  submitCallback,
  chatContext,
  stopResponseCallback,
  isSimpleVersion,
  placeholder,
  disableEditingOnMessagePending,
  persistInputTextOnSubmit,
  clearDocumentContextOnSubmit,
}: ChatSubmitProps) => {
  const { t } = useTranslation(['chat', 'intel', 'common']);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const chatSubmitNavigation = useChatSubmitNavigate();
  const { navigateToChatMessage } = useNavigateToAssetOverview();

  const { sendMessage, isMessagePending, resetStore, messages, abortRequest } =
    useChat(state => ({
      sendMessage: state.sendMessage,
      isMessagePending: state.isMessagePending,
      messages: state.messages,
      abortRequest: state.abortRequest,
      resetStore: state.resetStore,
    }));

  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    chatSubmitNavigation(lastMessage);
  }, [messages, chatSubmitNavigation]);

  const shouldResetSessionFromQuestion = (question: string) => {
    const restRegex = new RegExp(`^${t('chat:resetSession')}$`, 'i');
    return restRegex.test(question);
  };

  const submitHandler = (
    inputValue: string,
    onSubmitCallbackHandler: () => void
  ) => {
    if (isMessagePending) {
      return;
    }

    if (inputValue) {
      const question = inputValue.trim();

      if (shouldResetSessionFromQuestion(question)) {
        resetStore();
      } else if (chatContext?.target) {
        sendMessage({
          question,
          target: chatContext.target,
          ...(!!chatContext?.documents?.length && {
            documents: chatContext.documents,
          }),
        });
      } else {
        sendMessage({ question });
      }

      onSubmitCallbackHandler();

      if (inputRef.current) {
        inputRef.current.rows = 1;
      }
    }
  };

  const handleChatSubmit = (
    event: React.FormEvent<HTMLFormElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    event.preventDefault();

    if (isMessagePending) {
      stopResponseCallback?.();
      return;
    }

    submitHandler(inputText, onSubmitCallbackHandler);
    submitCallback?.(inputText.trim());
  };

  const onReset = () => {
    resetStore();
  };

  const handleKeydown = (
    _: React.KeyboardEvent<HTMLElement>,
    inputText: string,
    onSubmitCallbackHandler: () => void
  ) => {
    submitHandler(inputText, onSubmitCallbackHandler);
  };

  const handSelectItem = (item: ChatDropdown, clear: () => void) => {
    navigateToChatMessage(item.tag);
    clear();
  };

  return (
    <TagSubmit
      ref={inputRef}
      headerText={t('chat:dropdown.header')}
      onSubmit={handleChatSubmit}
      onKeyDown={handleKeydown}
      isLoading={isMessagePending}
      onAbort={abortRequest}
      onReset={onReset}
      onSelect={handSelectItem}
      isSimpleVersion={isSimpleVersion}
      placeholder={placeholder}
      placement="bottom-start"
      disableEditingOnMessagePending={disableEditingOnMessagePending}
      persistInputTextOnSubmit={persistInputTextOnSubmit}
      clearDocumentContextOnSubmit={clearDocumentContextOnSubmit}
    />
  );
};
